/**
 * Agent Performance Dashboard
 * Admin → Operations → Agent Performance
 * Tracks booking agent productivity and KPIs
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  ArrowLeft, Users, Package, CreditCard, TrendingUp, Download,
  MessageCircle, Mail, Target, Activity, Clock, ChevronRight,
  Loader2, RefreshCw, BarChart3, CheckCircle, AlertTriangle, XCircle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AgentPerformance = () => {
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState("this_month");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [agentsData, setAgentsData] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, [dateFilter]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const [dashRes, agentsRes] = await Promise.all([
        fetch(`${API}/api/admin/agent-performance/dashboard?date_filter=${dateFilter}`),
        fetch(`${API}/api/admin/agent-performance/agents?date_filter=${dateFilter}`)
      ]);
      
      const dashJson = await dashRes.json();
      const agentsJson = await agentsRes.json();
      
      if (dashJson.success) setDashboardData(dashJson.cards);
      if (agentsJson.success) setAgentsData(agentsJson.agents);
    } catch (err) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboard();
    setRefreshing(false);
    toast.success("Dashboard refreshed");
  };

  const fetchAgentDrilldown = async (agentId) => {
    setSelectedAgent(agentId);
    setDrilldownLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/agent-performance/agent/${agentId}/drilldown?date_filter=${dateFilter}`);
      const json = await res.json();
      if (json.success) {
        setDrilldownData(json);
      }
    } catch (err) {
      toast.error("Failed to load agent details");
    } finally {
      setDrilldownLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      under_target: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      needs_review: "bg-red-500/20 text-red-400 border-red-500/30"
    };
    const labels = {
      active: "Active",
      under_target: "Under Target",
      needs_review: "Needs Review"
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || styles.active}`}>
        {labels[status] || status}
      </span>
    );
  };

  const KPICard = ({ icon: Icon, label, value, suffix = "", color = "emerald", subtext }) => (
    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 bg-${color}-500/20 rounded-lg flex items-center justify-center`}>
          <Icon className={`h-5 w-5 text-${color}-400`} />
        </div>
        <span className="text-slate-400 text-sm">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">
        {typeof value === 'number' ? value.toLocaleString() : value}{suffix}
      </p>
      {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-slate-400">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-amber-500" />
                  Agent Performance
                </h1>
                <p className="text-slate-400 text-sm">Operations → Agent Performance</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="today" className="text-white">Today</SelectItem>
                  <SelectItem value="this_week" className="text-white">This Week</SelectItem>
                  <SelectItem value="this_month" className="text-white">This Month</SelectItem>
                  <SelectItem value="all" className="text-white">All Time</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="border-slate-700">
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* KPI Cards Grid */}
        {dashboardData && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <KPICard icon={Package} label="Total Bookings" value={dashboardData.total_bookings} color="blue" />
            <KPICard icon={CheckCircle} label="Paid Bookings" value={dashboardData.paid_bookings} color="emerald" />
            <KPICard icon={Clock} label="Pending Payment" value={dashboardData.unpaid_bookings} color="amber" />
            <KPICard icon={TrendingUp} label="Conversion Rate" value={dashboardData.conversion_rate} suffix="%" color="purple" />
            <KPICard icon={CreditCard} label="Total Value" value={`J$${dashboardData.total_booking_value?.toLocaleString()}`} color="emerald" />
            <KPICard icon={Download} label="Labels Downloaded" value={dashboardData.labels_downloaded} color="blue" />
            <KPICard icon={Mail} label="Payment Links Sent" value={dashboardData.payment_links_sent} color="cyan" />
            <KPICard icon={MessageCircle} label="WhatsApp Shares" value={dashboardData.whatsapp_shares} color="green" />
            <KPICard icon={Mail} label="Email Shares" value={dashboardData.email_shares} color="indigo" />
            <KPICard icon={Activity} label="Avg Bookings/Day" value={dashboardData.avg_bookings_per_day} color="amber" />
            <KPICard icon={Users} label="Active Agents" value={dashboardData.active_agents} color="purple" />
            <KPICard icon={Target} label="Weekly Target" value={dashboardData.target_attainment} suffix="%" color="rose" />
          </div>
        )}

        {/* Agents Table */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="p-4 border-b border-slate-700/50">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-amber-500" />
              Per-Agent Performance
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="agents-table">
              <thead className="bg-slate-800">
                <tr className="text-left text-xs text-slate-400 uppercase">
                  <th className="px-4 py-3">Agent</th>
                  <th className="px-4 py-3">Bookings</th>
                  <th className="px-4 py-3">Paid</th>
                  <th className="px-4 py-3">Conversion</th>
                  <th className="px-4 py-3">Taxi Ops</th>
                  <th className="px-4 py-3">PUDO</th>
                  <th className="px-4 py-3">ClockInJA</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {agentsData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                      No agents found. Add agents in Operations → Booking Agents.
                    </td>
                  </tr>
                ) : (
                  agentsData.map((agent) => (
                    <tr 
                      key={agent.id} 
                      className="hover:bg-slate-700/30 cursor-pointer transition-colors"
                      onClick={() => fetchAgentDrilldown(agent.id)}
                      data-testid={`agent-row-${agent.id}`}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white font-medium">{agent.name}</p>
                          <p className="text-slate-500 text-xs">{agent.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-white">{agent.bookings_created}</td>
                      <td className="px-4 py-3 text-emerald-400">{agent.paid_bookings}</td>
                      <td className="px-4 py-3">
                        <span className={agent.conversion_rate >= 70 ? 'text-emerald-400' : agent.conversion_rate >= 50 ? 'text-amber-400' : 'text-red-400'}>
                          {agent.conversion_rate}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{agent.taxi_operators_onboarded}</td>
                      <td className="px-4 py-3 text-slate-300">{agent.pudo_centres_onboarded}</td>
                      <td className="px-4 py-3">
                        <span className="text-slate-300">{agent.clockinja_leads_generated}</span>
                        <span className="text-slate-500 text-xs ml-1">({agent.clockinja_leads_converted} conv)</span>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(agent.status)}</td>
                      <td className="px-4 py-3">
                        <ChevronRight className="h-4 w-4 text-slate-500" />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Agent Drilldown Modal/Panel */}
        {selectedAgent && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedAgent(null)}>
            <div className="bg-slate-900 rounded-2xl border border-slate-700 max-w-3xl w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Agent Details</h3>
                <Button variant="ghost" size="sm" onClick={() => setSelectedAgent(null)}>
                  <XCircle className="h-5 w-5 text-slate-400" />
                </Button>
              </div>
              
              {drilldownLoading ? (
                <div className="p-8 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
                </div>
              ) : drilldownData ? (
                <div className="p-4 overflow-y-auto max-h-[60vh] space-y-4">
                  {/* Agent Info */}
                  <div className="bg-slate-800/50 rounded-xl p-4">
                    <h4 className="text-white font-medium">{drilldownData.agent?.name}</h4>
                    <p className="text-slate-400 text-sm">{drilldownData.agent?.email}</p>
                    <p className="text-slate-500 text-xs mt-1">{drilldownData.agent?.phone}</p>
                  </div>
                  
                  {/* Productivity Trend */}
                  <div className="bg-slate-800/50 rounded-xl p-4">
                    <h4 className="text-white font-medium mb-3">Weekly Trend</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {drilldownData.productivity_trend?.map((week, i) => (
                        <div key={i} className="bg-slate-900 rounded-lg p-3 text-center">
                          <p className="text-slate-500 text-xs">{week.week_start?.slice(5)}</p>
                          <p className="text-white font-bold text-lg">{week.bookings}</p>
                          <p className="text-xs text-emerald-400">{week.conversion}%</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Recent Bookings */}
                  <div className="bg-slate-800/50 rounded-xl p-4">
                    <h4 className="text-white font-medium mb-3">Recent Bookings ({drilldownData.bookings?.length})</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {drilldownData.bookings?.slice(0, 10).map((booking) => (
                        <div key={booking.id} className="flex items-center justify-between p-2 bg-slate-900 rounded-lg">
                          <div>
                            <p className="text-white text-sm font-mono">{booking.tracking_id}</p>
                            <p className="text-slate-500 text-xs">{booking.sender_name} → {booking.recipient_name}</p>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm ${booking.is_paid ? 'text-emerald-400' : 'text-amber-400'}`}>
                              {booking.is_paid ? 'Paid' : 'Pending'}
                            </p>
                            <p className="text-slate-500 text-xs">J${booking.total_price?.toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AgentPerformance;
